import { getTimeDefinitionService } from '../../../core/services/TimeDefinitionService.js';
import { resolveTextPlaceholders, hasAnySupportedPlaceholder } from '../../utils/placeholder-resolver.js';
import { rosterHasResolvableNames } from '../../utils/character-name-tokens.js';
import { pickResolvedCharacterName } from '../components/character-name-utils.js';

const DISPLAY_SLOT_COUNT = 3;

const scenarioOutfits = (chars) => {
  const id = chars?.[0]?.outfit ?? chars?.[0]?.outfit_id;
  return id != null && id !== 0 ? [0, id] : [0];
};

export const hydrateScenarioCharsWithNames = (chars, nameSources = []) => {
  const nameById = new Map();
  nameSources.flat().forEach((character) => {
    if (!character) return;
    const id = character.id ?? character.character_id;
    const name = character.name || character.character_name;
    if (id == null || !name) return;
    nameById.set(String(id), name);
  });
  return (Array.isArray(chars) ? chars : []).map((char) => {
    const id = char?.id ?? char?.character_id;
    const name = nameById.get(String(id)) || char?.name;
    return name ? { ...char, name } : char;
  });
};

const selectedSlotName = (character) => {
  if (!character || typeof character !== 'object') return '';
  const id = character.id ?? character.character_id;
  return pickResolvedCharacterName(character, id) || '';
};

export const overlaySelectedCharacterSlots = (scenarioChars = [], selectedChars = []) => {
  const base = Array.isArray(scenarioChars) ? scenarioChars : [];
  const selected = (Array.isArray(selectedChars) ? selectedChars : []).slice(0, DISPLAY_SLOT_COUNT);
  const hasSelectedName = selected.some((character) => selectedSlotName(character));
  if (!hasSelectedName) return base;

  const slotCount = Math.min(DISPLAY_SLOT_COUNT, Math.max(base.length, selected.length));
  const merged = [];
  for (let index = 0; index < slotCount; index += 1) {
    const scenarioChar = base[index];
    const name = selectedSlotName(selected[index]);
    if (name) {
      const selectedChar = selected[index];
      merged.push({
        ...(scenarioChar && typeof scenarioChar === 'object' ? scenarioChar : {}),
        id: selectedChar.id ?? selectedChar.character_id ?? scenarioChar?.id,
        name
      });
      continue;
    }
    if (scenarioChar) merged.push(scenarioChar);
  }
  return merged;
};

export const resolveScenarioDisplayText = (text, chars) => {
  if (typeof text !== 'string' || !text) return text || '';
  if (!hasAnySupportedPlaceholder(text) || !rosterHasResolvableNames(chars)) return text;
  return resolveTextPlaceholders(text, { scenarioChars: chars });
};

export const resolveScenarioRecordDisplay = (record, nameSources = [], selectedChars = []) => {
  if (!record) return record;
  const chars = hydrateScenarioCharsWithNames(record.chars, [record.chars, ...nameSources]);
  const displayChars = overlaySelectedCharacterSlots(chars, selectedChars);
  return {
    ...record,
    chars,
    name: resolveScenarioDisplayText(record.name || '', displayChars),
    description: resolveScenarioDisplayText(record.description || record.descr || '', displayChars)
  };
};

export const hydrateScenarioRecord = (record, nameSources = []) => {
  if (!record) return record;
  return {
    ...record,
    chars: hydrateScenarioCharsWithNames(record.chars, [record.chars, ...nameSources])
  };
};

export const mapScenarioRecord = (s, { isPrivate = false } = {}) => {
  const chars = s.chars || [];
  return {
    id: s.id,
    name: s.name,
    description: s.description || s.descr || '',
    pop: Number.isFinite(Number(s.pop)) ? Number(s.pop) : 0,
    sprite_url: chars[0]?.sprite_url || s.url || null,
    outfits: scenarioOutfits(chars),
    chars,
    tags: Array.isArray(s.tags) ? s.tags : [],
    isN: s.isN ?? (s.is_nsfw ? 1 : 0),
    is_nsfw: s.is_nsfw === true || s.isN === 1 || s.isN === true,
    loc: s.loc,
    loc_id: s.loc_id || s.loc,
    time: s.time,
    ...(isPrivate ? { is_private: true } : {}),
  };
};

export const sortScenariosByCharacterRelevance = (scenarios, characterId) => {
  if (!scenarios || !characterId) return scenarios || [];

  const numericCharacterId = parseInt(characterId, 10);

  return [...scenarios].sort((a, b) => {
    const aHasCharacter = a.chars && a.chars.some((char) => parseInt(char.id, 10) === numericCharacterId);
    const bHasCharacter = b.chars && b.chars.some((char) => parseInt(char.id, 10) === numericCharacterId);

    if (aHasCharacter === bHasCharacter) {
      const aPopularity = Number.isFinite(Number(a?.pop)) ? Number(a.pop) : 0;
      const bPopularity = Number.isFinite(Number(b?.pop)) ? Number(b.pop) : 0;
      if (aPopularity !== bPopularity) return bPopularity - aPopularity;

      const aId = Number.isFinite(Number(a?.id)) ? Number(a.id) : 0;
      const bId = Number.isFinite(Number(b?.id)) ? Number(b.id) : 0;
      return aId - bId;
    }

    return aHasCharacter ? -1 : 1;
  });
};

const toPublicSpot = (s, imageUrl, background) => ({
  id: s.id,
  name: s.name,
  description: s.description || '',
  imageUrl,
  sprite_url: s.chars?.[0]?.sprite_url || s.sprite_url || null,
  loc_id: s.loc_id || 999,
  outfits: (s.outfits || []).map((o) => o.toString()),
  background,
  tags: Array.isArray(s.tags) ? s.tags : [],
  chars: Array.isArray(s.chars) ? s.chars : [],
  isN: s.isN ?? (s.is_nsfw ? 1 : 0),
  is_nsfw: s.is_nsfw === true || s.isN === 1 || s.isN === true,
  charCount: Array.isArray(s.chars) ? s.chars.length : 0,
  first_char_gender: s.chars?.[0]?.gender ?? null
});

export const mapScenariosToSpots = (scenarios, imageUrls, getBackgroundUrl, nameSources = [], selectedChars = []) =>
  scenarios.map((s) => {
    const resolved = resolveScenarioRecordDisplay(s, nameSources, selectedChars);
    return {
      ...toPublicSpot(
        resolved,
        imageUrls[s.id] || '',
        getBackgroundUrl(s.loc_id || 999, s.time || 'night')
      ),
      tokenName: s.name || '',
      tokenDescription: s.description || s.descr || ''
    };
  });

export const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[j], shuffled[i]] = [shuffled[i], shuffled[j]];
  }
  return shuffled;
};

export const resolveConversationBackdrop = (scenarioDetails, finalState = {}) => {
  const locId = scenarioDetails?.loc_id ?? finalState.loc_id ?? 0;
  const timeSlot = scenarioDetails?.time || 3;
  const mappedTime = getTimeDefinitionService().getTimeOfDayFromNumericSlot(String(timeSlot));
  const timeOfDay = finalState.timeOfDay || mappedTime || 'night';
  return { locId: locId || 0, timeOfDay, timeSlot };
};
